import { useState } from 'react';

import {
  type GestionConcoursDocxPreview,
  applyGestionConcoursDocxApi,
  previewGestionConcoursDocxApi,
  sendGestionConcoursFormatReportApi,
} from '@/lib/api/competitionsApi';
import { AlertTriangle, CheckCircle2, FileText, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const MAX_DOCX_MB = 5;

export default function GestionConcoursImportPanel({
  competitionId,
  onApplied,
}: {
  competitionId: number;
  onApplied: () => void;
}) {
  const [preview, setPreview] = useState<GestionConcoursDocxPreview | null>(null);
  const [fileName, setFileName] = useState('');
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [error, setError] = useState('');

  async function handleUpload(file?: File) {
    if (!file) return;
    setError('');
    setPreview(null);
    setShared(false);
    if (!file.name.toLowerCase().endsWith('.docx') || file.size > MAX_DOCX_MB * 1024 * 1024) {
      setError('Choisissez un document Word (.docx) de moins de 5 Mo.');
      return;
    }
    setFileName(file.name);
    setReading(true);
    try {
      setPreview(await previewGestionConcoursDocxApi(competitionId, file));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de lire ce document.');
    } finally {
      setReading(false);
    }
  }

  async function handleApply() {
    if (!preview || !preview.eligible) return;
    setSaving(true);
    setError('');
    try {
      const result = await applyGestionConcoursDocxApi(competitionId, preview.previewId);
      toast.success(result.message);
      setPreview(null);
      onApplied();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de mettre à jour les résultats.');
    } finally {
      setSaving(false);
    }
  }

  async function handleShare() {
    if (!preview || shared) return;
    setSharing(true);
    try {
      await sendGestionConcoursFormatReportApi(competitionId, preview.previewId);
      setShared(true);
      toast.success('Merci pour votre retour !');
    } catch (e) {
      setError(e instanceof Error ? e.message : "L'envoi du rapport a échoué.");
    } finally {
      setSharing(false);
    }
  }

  const documentChanges = preview?.documentChanges;
  const changedLines = documentChanges
    ? documentChanges.added.length + documentChanges.changed.length + documentChanges.removed.length
    : 0;
  const offerReport =
    preview && !shared && (preview.formatChanged || !preview.eligible || (preview.fileChanged && changedLines > 0));

  return (
    <section className='mb-6 rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5'>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <div>
          <h2 className='flex items-center gap-2 text-base font-bold text-indigo-950'>
            <FileText className='h-5 w-5' /> Importer les résultats depuis Gestion Concours
          </h2>
          <p className='mt-1 text-sm text-slate-600'>
            Choisissez le fichier Word des résultats. BoulesConnect reconnaît les équipes et vous montre les changements
            avant de les enregistrer.
          </p>
        </div>
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-xl bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-800 ${reading ? 'pointer-events-none opacity-60' : ''}`}
        >
          <Upload className='h-4 w-4' /> {reading ? 'Lecture en cours…' : 'Choisir un fichier Word'}
          <input
            className='sr-only'
            type='file'
            accept='.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            disabled={reading}
            onChange={e => {
              const file = e.target.files?.[0];
              e.target.value = '';
              void handleUpload(file);
            }}
          />
        </label>
      </div>
      {error && !preview && (
        <p role='alert' className='mt-3 text-sm text-red-700'>
          {error}
        </p>
      )}

      {preview && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4' role='presentation'>
          <div
            role='dialog'
            aria-modal='true'
            aria-labelledby='gc-import-title'
            className='max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl'
          >
            <div className='mb-4 flex items-start justify-between gap-4'>
              <div>
                <h2 id='gc-import-title' className='text-xl font-bold text-slate-950'>
                  Vérifier les résultats à importer
                </h2>
                <p className='mt-1 break-all text-sm text-slate-500'>{fileName}</p>
              </div>
              <button
                aria-label="Fermer l'aperçu"
                className='rounded-lg p-2 hover:bg-slate-100'
                onClick={() => {
                  setPreview(null);
                  setError('');
                }}
                disabled={saving}
              >
                <X className='h-5 w-5' />
              </button>
            </div>

            <div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
              <div className='rounded-xl bg-slate-100 p-3'>
                <p className='text-xs text-slate-500'>Rencontres lues</p>
                <p className='text-2xl font-bold'>{preview.totalMatches}</p>
              </div>
              <div className='rounded-xl bg-slate-100 p-3'>
                <p className='text-xs text-slate-500'>Équipes reconnues</p>
                <p className='text-2xl font-bold'>
                  {preview.matchedTeams} / {preview.totalTeams}
                </p>
              </div>
              <div className='rounded-xl bg-slate-100 p-3'>
                <p className='text-xs text-slate-500'>Classements à actualiser</p>
                <p className='text-2xl font-bold'>{preview.totalRankChanges}</p>
              </div>
            </div>

            {preview.eligible ? (
              <p className='mt-4 flex items-center gap-2 text-sm font-semibold text-emerald-800'>
                <CheckCircle2 className='h-4 w-4' /> Toutes les équipes sont identifiées. Vous pouvez confirmer
                l'import.
              </p>
            ) : (
              <div className='mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900'>
                <p className='flex items-center gap-2 font-semibold'>
                  <AlertTriangle className='h-4 w-4' /> Certains résultats doivent être vérifiés.
                </p>
                <p className='mt-1'>
                  Aucun classement ne sera modifié. Vérifiez que ce document correspond au bon concours et que les noms
                  des joueurs sont identiques.
                </p>
                {preview.unmatched.slice(0, 6).map((team, i) => (
                  <p key={`${team.round}-${i}`} className='mt-2'>
                    {team.players.join(', ')} —{' '}
                    {team.reason === 'AMBIGUOUS' ? 'plusieurs équipes possibles' : 'équipe introuvable'}
                  </p>
                ))}
                {preview.issues.slice(0, 5).map((issue, i) => (
                  <p key={i} className='mt-2'>
                    {issue.message}
                  </p>
                ))}
              </div>
            )}

            {!preview.firstImport && (
              <p className='mt-4 text-sm text-slate-700'>
                {preview.unchangedDocument
                  ? 'Ce document a déjà été importé sans modification.'
                  : changedLines > 0
                    ? `${changedLines} rencontre(s) ajoutée(s), supprimée(s) ou modifiée(s) depuis le dernier import.`
                    : 'Le fichier a changé, mais aucun changement de résultat n’a été détecté.'}
              </p>
            )}
            {preview.formatChanged && (
              <p className='mt-2 text-sm font-semibold text-amber-800'>
                La présentation du fichier a changé depuis le dernier import.
              </p>
            )}
            {preview.rankChanges.length > 0 && (
              <div className='mt-4'>
                <h3 className='font-semibold text-slate-900'>Aperçu des classements</h3>
                <div className='mt-2 max-h-56 overflow-auto rounded-xl border border-slate-200'>
                  <table className='w-full text-left text-sm'>
                    <thead className='sticky top-0 bg-slate-50 text-xs font-semibold text-slate-600'>
                      <tr>
                        <th className='p-2'>Équipe</th>
                        <th className='p-2'>Avant</th>
                        <th className='p-2'>Après</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rankChanges.slice(0, 25).map(r => (
                        <tr key={r.teamId} className='border-t border-slate-100'>
                          <td className='p-2'>{r.name}</td>
                          <td className='p-2'>{r.previousRank ?? '—'}</td>
                          <td className='p-2 font-semibold'>{r.newRank}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.totalRankChanges > 25 && (
                  <p className='mt-1 text-xs text-slate-500'>
                    Les 25 premiers changements sont affichés sur {preview.totalRankChanges}.
                  </p>
                )}
              </div>
            )}
            {offerReport && (
              <div className='mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4'>
                <p className='text-sm font-semibold text-slate-900'>
                  Aidez BoulesConnect à mieux lire les fichiers Word
                </p>
                <p className='mt-1 text-sm text-slate-600'>
                  Vous pouvez nous transmettre un rapport sur la présentation du document. Les noms des joueurs et le
                  fichier Word ne sont pas envoyés.
                </p>
                <button
                  className='mt-3 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:bg-slate-100'
                  disabled={sharing}
                  onClick={() => void handleShare()}
                >
                  {sharing ? 'Envoi…' : 'Envoyer le rapport à BoulesConnect'}
                </button>
              </div>
            )}
            {shared && <p className='mt-4 text-sm text-emerald-700'>Merci, votre retour a été transmis.</p>}
            {error && (
              <p role='alert' className='mt-4 text-sm text-red-700'>
                {error}
              </p>
            )}
            <div className='mt-6 flex justify-end gap-3'>
              <button
                className='rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold'
                disabled={saving}
                onClick={() => {
                  setPreview(null);
                  setError('');
                }}
              >
                Annuler
              </button>
              <button
                className='rounded-xl bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40'
                disabled={!preview.eligible || (preview.unchangedDocument && preview.totalRankChanges === 0) || saving}
                onClick={() => void handleApply()}
              >
                {saving
                  ? 'Enregistrement…'
                  : preview.totalRankChanges > 0
                    ? `Confirmer ${preview.totalRankChanges} changement(s)`
                    : 'Valider ce document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
